// [ START OF MODULE - VER 2.5 ( src/context/ThemeContext.jsx ) ]
import React, { useState, useEffect } from 'react';
import { themes } from './themes';
import { ThemeContext } from './useTheme';

export const ThemeProvider = ({ children }) => {
  const [themePreference, setThemePreference] = useState(() => {
    const stored = localStorage.getItem('tiger_theme_pref') || 'SYSTEM';
    // Guard: normalise legacy lowercase values (e.g. 'dark' → 'DARK')
    return stored.toUpperCase();
  });

  const [currentTheme, setCurrentTheme] = useState('DARK');

  useEffect(() => {
    const root = document.documentElement;
    
    const applyTheme = (pref) => {
      let resolvedTheme = (pref || 'SYSTEM').toUpperCase();
      
      if (resolvedTheme === 'SYSTEM') {
        resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'DARK' : 'LIGHT';
      }

      // Safety guard — if somehow an invalid key slips through, default to DARK
      if (!themes[resolvedTheme]) resolvedTheme = 'DARK';
      
      setCurrentTheme(resolvedTheme); 
      
      const themeVars = themes[resolvedTheme];
      Object.entries(themeVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });

      if (themeVars['--font-main']) {
        root.style.fontFamily = themeVars['--font-main'];
      }
      
      // Set both data-theme attribute AND class so both CSS patterns work
      root.setAttribute('data-theme', resolvedTheme.toLowerCase());
      root.classList.remove('DARK', 'LIGHT', 'SYSTEM', 'dark', 'light');
      root.classList.add(resolvedTheme);
    };

    applyTheme(themePreference);
    localStorage.setItem('tiger_theme_pref', themePreference);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themePreference === 'SYSTEM') {
        applyTheme('SYSTEM');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  const toggleTheme = () => {
    setThemePreference(prev => {
      if (prev === 'DARK') return 'LIGHT';
      if (prev === 'LIGHT') return 'SYSTEM';
      return 'DARK';
    });
  };

  return (
    <ThemeContext.Provider value={{ themePreference, setThemePreference, currentTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/*
=========================================
CHANGE MANIFEST
=========================================
VER 2.5 - 2026-09-09 - BUILD-2026.902 - RMI-2026-568: light-mode near-white surfaces toned down
- USER: "in light mode can we make the white color less bright...it is
  blinding at times." --bg-sidebar/--bg-card/--bg-input were all ~96-98%
  luminance -- brighter than --bg-main (the page floor, ~90-94%) -- with
  --bg-input the brightest of all and stacked dozens-deep on every form-heavy
  screen (WO editor, manifest panel, SRQ forms).
- Darkened all three ~5-6%, staying inside the same pastel-tinted language
  --bg-main already established (RMI-2026-423/424) rather than introducing a
  plain grey:
    --bg-sidebar  #F5F8F9->#EBF0F4  ->  #EBEFF2->#E1E7ED
    --bg-card     #F6F7F9->#EEEBF4  ->  #ECEEF1->#E4E0EA
    --bg-input    #F9FAFB->#F3F5F7  ->  #EDEFF1->#E7EAEC
  DARK theme untouched. Token values only, no structural/component change.
=========================================
VER 2.4 - 2026-08-17 - RMI-2026-486: navbar strip, purple-forward gradient
- User: "Change the background color of the navbar strip on the command
  center. open to reccomendations." --bg-navbar was an exact duplicate of
  --bg-sidebar in both themes, so the strip had no visual identity of its
  own. Iterated visually via Artifact (5 dark candidates, then 3 light
  candidates after the first light pass read as too washed out) before
  touching code.
    DARK  --bg-navbar  90deg #124468->#1B557E  ->  90deg #1B557E->#331579
    LIGHT --bg-navbar  90deg #F5F8F9->#EBF0F4  ->  90deg #CFE0EF->#DCCFF3
  Both stay inside the same blue->purple relationship (eases into the
  purple already used for active-tab icons and the IRIS pill), just scaled
  light vs dark. --bg-sidebar unchanged in both themes.
=========================================
VER 2.3 - 2026-08-16 - RMI-2026-470: LOB badge "blue on blue" contrast fix (DARK only)
- User: "the problem is the blue on blue" -- screenshots showed the
  TECHNOLOGY LOB badge (MissionFeedCard.jsx, MobileWorkspaceModal.jsx)
  nearly invisible. Root cause: --bg-card is a blue-to-purple brand
  gradient (#155079 -> #331579); the original --lob-tech-bg was a
  low-opacity (0.12) same-hue blue tint, and --lob-tech-text a medium blue
  (#3B82F6) -- blended together on top of an already-blue card, contrast
  collapsed. --lob-hybrid (purple) had the identical structural issue
  against the gradient's purple half.
- DARK theme only:
    --lob-tech-bg      rgba(59,130,246,0.12)  -> rgba(37,99,235,0.38)
    --lob-tech-text    #3B82F6                -> #EFF6FF
    --lob-tech-border  rgba(59,130,246,0.30)  -> rgba(147,197,253,0.65)
    --lob-hybrid-bg    rgba(130,74,248,0.10)  -> rgba(130,74,248,0.35)
    --lob-hybrid-text  #824AF8                -> #F5F3FF
    --lob-hybrid-border rgba(130,74,248,0.22) -> rgba(196,166,253,0.65)
  Higher bg opacity + near-white text of the same hue holds contrast
  regardless of where on the gradient the badge sits, instead of relying
  on a transparent wash that only works over a neutral surface.
  --lob-logistics (green) and --lob-default (gray) don't share this hue
  collision with the gradient -- left untouched. LIGHT theme's card bg is
  near-white, not affected by this bug -- untouched.
=========================================
VER 2.2 - 2026-07-22 - BUILD-2026.603 - RMI-2026-344: export themes map for PDF export
- Added the export keyword to the themes const so ReportViewerModal.jsx can
  import { themes } and force LIGHT-mode CSS variables onto an off-screen clone
  of the report body before rasterizing it for PDF export -- otherwise the PDF
  baked in whatever mode (dark or light) the user happened to be viewing in.
- No other change: DARK/LIGHT token values, ThemeProvider, useTheme, toggleTheme
  all untouched.
- Inline changelog trimmed to last 5 entries per house style; VER 1.7/1.6
  archived to docs/INLINE_CHANGELOG_ARCHIVE.md.
=========================================
VER 2.1 - 2026-07-12 - BUILD-2026.583 - RMI-2026-325: brand alignment (accent-primary, coral, Poppins)
- Confirmed against the official brand guideline (Tiger_Technology___Logistics_Brand_Guideline_V01,
  09/15/2021) that purple (#824AF8) is a core identity color alongside blue (#5899CD) and coral
  (#F37664) -- not a decorative or secondary accent. This reverses part of VER 1.x's dark-mode
  contrast fix, which had swapped --accent-primary to blue in dark mode instead of tuning purple.
    DARK  --accent-primary   #60A5FA (blue) -> #A78BFA (lightened purple, same contrast tier as
                                                  the blue it replaces)
    DARK  --tint-primary        rgba(96,165,250,*)  -> rgba(167,139,250,*)  (recomputed to match
    DARK  --tint-primary-border rgba(96,165,250,*)  -> rgba(167,139,250,*)   new accent-primary hue)
  LIGHT --accent-primary unchanged (#6D28D9, already purple family).
- Added brand coral to the token set via --accent-courier, which previously used green and had
  no coral presence anywhere in the app despite coral being 1/3 of the brand identity gradient.
    DARK  --accent-courier  #10B981 (green) -> #F37664 (brand coral)
    LIGHT --accent-courier  #059669 (green) -> #C2604F (deeper coral for light-bg contrast)
  --accent-success (semantic completion color) intentionally untouched -- this only affects the
  LOB/brand-tier courier token, not status/completion coloring.
- --font-main changed Inter -> Poppins in both themes, matching the guideline's external-facing
  font spec (a Claude session on Feb 28, 2026 had already correctly extracted this from the live
  site; it had since drifted back to Inter).
- Known follow-on, not fixed here: some components may reference the old blue accent-primary via
  hardcoded rgba(96,165,250,...) literals rather than var(--tint-primary) -- those will not pick
  up this change and should be swept in a future pass if found.
=========================================
*/
/*
=========================================
CHANGE MANIFEST
=========================================
*/
// [ END OF MODULE - VER 2.5 ( src/context/ThemeContext.jsx ) ]
